import { useAtom } from 'jotai';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { UserTheme } from '@gitbook/app-database';
import type { NavigationScreenState } from '@gitbook/app-navigation';
import {
    createPalette,
    type PaletteCommandDef,
    PaletteCommandType,
    type PaletteGroupDef,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { useEventCallback } from '@gitbook/util-react-hooks';

import {
    getOrganizationPlanBadgeProps,
    getSiteNavigationTarget,
    OrganizationIcon,
    SiteContentIcon,
    SiteTypeBadge,
} from '../../Components';
import { useNavigation } from '../../navigation';
import { apiResource, readAPICache, useAuthOperations, userThemeAtom } from '../../state';

/**
 * Settings palette accessible from the sidebar header.
 */
export function useGetSettingsPalette(props: {
    organization: api.Organization;
    organizationContent: api.OrganizationAllContent;
    onCreateSite?: () => void;
    screen: NavigationScreenState;
}) {
    const { onCreateSite, organization, organizationContent, screen } = props;
    const { signOut } = useAuthOperations();
    const { navigate } = useNavigation();
    const [userTheme, setUserTheme] = useAtom(userThemeAtom);

    const getOrganizationListPaletteGroups = useGetOrganizationListPaletteGroups();
    const getSiteListPaletteGroups = useGetSiteListPaletteGroups();
    return useEventCallback(() => {
        const groups: PaletteGroupDef[] = [];

        const switchTheme = (theme: UserTheme) => {
            setUserTheme(theme);
            analytics.logEvent({
                type: 'account_edit_theme',
                theme,
            });
        };

        const organizationSettingsCommands: PaletteCommandDef[] = [
            {
                key: 'org_settings.profile',
                title: 'Settings',
                icon: Icon.Settings,
                trigger: () => {
                    navigate({
                        screen: {
                            type: 'org_settings',
                            organization: organization.id,
                            tab: { type: 'profile' },
                        },
                    });
                },
            },
            ...(organization.permissions.admin
                ? [
                      {
                          key: 'org_settings.invite',
                          title: 'Invite your team',
                          icon: Icon.UserAdd,
                          trigger: () => {
                              navigate({
                                  overlay: {
                                      type: 'org_invite',
                                      organization: organization.id,
                                  },
                              });
                          },
                      },
                  ]
                : []),
        ];

        const trackingContext = {
            type: 'click',
            action: 'open',
            organization: organization.id,
            screen: screen.type,
            screenSection: 'org_menu',
        } satisfies Omit<analytics.ClickAnalyticsEvent, 'component'>;

        groups.push({
            key: 'general',
            title: organization.title,
            commands: [
                {
                    key: 'general.home',
                    title: 'Home',
                    icon: Icon.Home,
                    trigger: () => {
                        analytics.logEvent({
                            ...trackingContext,
                            component: 'home_link',
                        });
                        navigate({
                            screen: {
                                type: 'home',
                                organization: organization.id,
                            },
                        });
                    },
                },
                ...organizationSettingsCommands,
            ],
        });
        groups.push({
            key: 'organization_content',
            commands: [
                {
                    key: 'general.sites',
                    title: 'Docs sites',
                    icon: Icon.DocsSite,
                    descendant: () =>
                        createPalette({
                            groups: getSiteListPaletteGroups({
                                currentSiteId: 'site' in screen ? (screen.site ?? null) : null,
                                organizationContent,
                                organizationId: organization.id,
                                onCreateSite,
                                trackingContext,
                            }),
                        }),
                },
                {
                    key: 'general.content',
                    title: 'Content',
                    icon: Icon.AllSpaces,
                    trigger: () => {
                        navigate({
                            screen: {
                                type: 'org_content',
                                organization: organization.id,
                            },
                        });
                    },
                },
            ],
        });

        groups.push({
            key: 'personal_settings',
            commands: [
                {
                    key: 'personal_settings.switch',
                    icon: Icon.Modes,
                    title: 'Theme',
                    descendant: () =>
                        createPalette({
                            static: true,
                            groups: [
                                {
                                    key: 'theme',
                                    commands: [
                                        {
                                            key: 'theme.default',
                                            title: 'System',
                                            icon: Icon.DeviceDesktop,
                                            type: PaletteCommandType.RADIO,
                                            selected: userTheme === UserTheme.Default,
                                            trigger: () => switchTheme(UserTheme.Default),
                                        },
                                        {
                                            key: 'theme.light',
                                            title: 'Light',
                                            icon: Icon.ThemeLight,
                                            type: PaletteCommandType.RADIO,
                                            selected: userTheme === UserTheme.Light,
                                            trigger: () => switchTheme(UserTheme.Light),
                                        },
                                        {
                                            key: 'theme.dark',
                                            title: 'Dark',
                                            icon: Icon.ThemeDark,
                                            type: PaletteCommandType.RADIO,
                                            selected: userTheme === UserTheme.Dark,
                                            trigger: () => switchTheme(UserTheme.Dark),
                                        },
                                    ],
                                },
                            ],
                        }),
                },
            ],
        });

        groups.push({
            key: 'account',
            commands: [
                {
                    key: 'switch-organization',
                    icon: Icon.Organization,
                    title: 'Switch organization',
                    descendant: () =>
                        createPalette({
                            groups: getOrganizationListPaletteGroups({
                                organizationId: organization.id,
                            }),
                        }),
                },
                {
                    key: 'account.signout',
                    icon: Icon.Leave,
                    title: 'Sign out',
                    trigger: () => {
                        signOut();
                    },
                },
            ],
        });

        return createPalette({ groups });
    });
}

function useGetOrganizationListPaletteGroups() {
    const { navigate } = useNavigation();
    return useEventCallback((props: { organizationId: string }): PaletteGroupDef[] => {
        const { organizationId } = props;
        return [
            {
                key: 'organizations',
                commands: () => {
                    const organizations = readAPICache(
                        apiResource('listOrganizationsForAuthenticatedUser', { limit: 100 })
                    );

                    return [
                        ...organizations.items.map((org) => {
                            return {
                                key: org.id,
                                title: org.title,
                                ...(org.id === organizationId
                                    ? {
                                          type: PaletteCommandType.RADIO as const,
                                          selected: true,
                                      }
                                    : {
                                          selected: undefined,
                                      }),
                                icon: <OrganizationIcon organization={org} />,
                                badge:
                                    getOrganizationPlanBadgeProps({
                                        plan: org.plan,
                                        trialing:
                                            org.trial.status === api.BillingTrialStatus.Active,
                                        blend: true,
                                    }) ?? undefined,
                                trigger: () => {
                                    navigate({
                                        screen: {
                                            type: 'home',
                                            organization: org.id,
                                        },
                                    });
                                },
                            };
                        }),
                        {
                            key: 'create-organization',
                            title: 'Create an organization',
                            icon: Icon.Organization,
                            trigger: () => {
                                analytics.logEvent({
                                    type: 'organization_open_create',
                                    reason: 'sidebar_menu',
                                });

                                navigate({
                                    screen: {
                                        type: 'welcome',
                                        hash: 'sidebar_menu',
                                    },
                                });
                            },
                        },
                    ];
                },
            },
        ];
    });
}

export function useGetSiteListPaletteGroups() {
    const { navigate } = useNavigation();
    return useEventCallback(
        (props: {
            currentSiteId: string | null;
            organizationContent: api.OrganizationAllContent;
            organizationId: string;
            onCreateSite?: () => void;
            trackingContext: Omit<analytics.ClickAnalyticsEvent, 'component'>;
        }): PaletteGroupDef[] => {
            const {
                currentSiteId,
                onCreateSite,
                organizationContent,
                organizationId,
                trackingContext,
            } = props;
            return [
                {
                    key: 'sites',
                    commands: organizationContent.sites.map((entry) => {
                        const { site } = entry;
                        return {
                            key: site.id,
                            title: site.title,
                            ...(site.id === currentSiteId
                                ? {
                                      type: PaletteCommandType.RADIO as const,
                                      selected: true,
                                  }
                                : {
                                      selected: undefined,
                                  }),
                            icon: <SiteContentIcon site={site} />,
                            suffix: (
                                <SiteTypeBadge
                                    type={site.type}
                                    appliedType={site.appliedType}
                                    size="xxsmall"
                                    weight="medium"
                                />
                            ),
                            trigger: () => {
                                if (site.id !== currentSiteId) {
                                    analytics.logEvent({
                                        ...trackingContext,
                                        site: site.id,
                                        component: 'site_item',
                                    });
                                }

                                navigate(
                                    getSiteNavigationTarget({
                                        organizationId,
                                        siteContent: entry,
                                    })
                                );
                            },
                        };
                    }),
                },
                ...(onCreateSite
                    ? [
                          {
                              key: 'create-site',
                              commands: [
                                  {
                                      key: 'create-site',
                                      title: 'Create a site',
                                      icon: Icon.Plus,
                                      trigger: () => {
                                          analytics.logEvent({
                                              ...trackingContext,
                                              component: 'create_site_item',
                                          });
                                          onCreateSite();
                                      },
                                  },
                              ],
                          },
                      ]
                    : []),
            ];
        }
    );
}
